import { useEffect, useRef, useState } from 'react';
import { nuevaClaveIntento } from '@renderer/lib/claveIntento';
import { conConfirmacionDeCarga } from '@renderer/lib/confirmarCargaRepetida';
import { X, AlertTriangle, Plus } from 'lucide-react';
import type { InsumoVista } from '@shared/types';
import { NumberField } from '@renderer/components/ui/NumberField';
import { useSessionStore } from '@renderer/store/useSessionStore';
import { traducirErrorBackend } from '@renderer/lib/mensajesError';

interface Props {
  insumo: InsumoVista;
  onClose: () => void;
  onOk: () => void;
}

/**
 * Sprint 15 — Mini-modal de carga individual por insumo.
 *
 * UX: una cosa a la vez. La dueña recibe pedidos del proveedor uno por uno
 * (pan, carne, refrescos) — el flujo natural es cargar uno por card cuando
 * llega cada cosa, no llenar 20 filas y "guardar todo".
 *
 * Previene el bug histórico 48.500 (Sprint 12.1e):
 *  - Si `cargadoHoy > 0`, muestra alerta ámbar prominente con el monto.
 *  - Resumen claro pre-confirmación: "Stock pasará de X a X + N".
 */
export function ModalCargaInsumo({ insumo, onClose, onOk }: Props) {
  const sesion = useSessionStore((s) => s.sesion);
  const [cantidad, setCantidad] = useState<number>(0);
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // ESC para cerrar
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const cargadoHoy = insumo.cargadoHoy ?? 0;
  const yaCargo = cargadoHoy > 0;
  const stockProyectado = insumo.stockActual + cantidad;
  const valido = cantidad > 0;

  // Sprint 165 — Un toque, una carga: el freno va ANTES del await (Sprint 159).
  const enviandoRef = useRef(false);
  const onGuardar = async () => {
    if (!sesion || !valido || enviandoRef.current) return;
    enviandoRef.current = true;
    setEnviando(true);
    setError(null);
    const clave = nuevaClaveIntento();
    const res = await conConfirmacionDeCarga((repetidaConfirmada) =>
      window.api.insumos.cargaDiaria({
        cargas: [{ insumoId: insumo.id, cargado: cantidad, mermaPct: 0 }],
        usuarioId: sesion.usuarioId,
        claveIntento: clave,
        repetidaConfirmada: repetidaConfirmada || undefined,
      }),
    );
    enviandoRef.current = false;
    setEnviando(false);
    if (res.ok) {
      onOk();
    } else {
      setError(traducirErrorBackend(res.error, res.codigo));
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl shadow-xl max-w-md w-full"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-slate-200 px-5 py-3">
          <h3 className="text-lg font-bold">Agregar stock</h3>
          <button onClick={onClose} className="btn-ghost p-2" aria-label="Cerrar">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="px-5 py-4 space-y-4">
          <div>
            <div className="text-xs uppercase tracking-wide text-slate-500">Ingrediente</div>
            <div className="text-xl font-bold text-slate-800">{insumo.nombre}</div>
            <div className="text-sm text-slate-500">
              Stock actual: <span className="font-mono font-semibold">{insumo.stockActual.toFixed(2)} {insumo.unidad}</span>
            </div>
          </div>

          {yaCargo && (
            <div className="rounded-lg bg-amber-50 border-2 border-amber-300 p-3 flex gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-700 shrink-0 mt-0.5" />
              <div className="text-sm text-amber-900">
                <strong>Ya cargaste hoy {cargadoHoy.toFixed(2)} {insumo.unidad}.</strong>
                <div className="text-xs mt-0.5">
                  Si vas a sumarle más, continúa. Si lo capturaste por error la vez pasada, mejor cancela y revisa el historial.
                </div>
              </div>
            </div>
          )}

          <div>
            <label className="label" htmlFor="cantNueva">
              ¿Cuánto NUEVO recibiste? ({insumo.unidad})
            </label>
            <NumberField
              id="cantNueva"
              min={0}
              decimals={3}
              className="input text-2xl font-mono text-right py-3"
              value={cantidad}
              onChange={setCantidad}
              autoFocus
            />
          </div>

          {valido && (
            <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-3 text-sm">
              <div className="font-semibold text-emerald-800 mb-1">Después de guardar:</div>
              <div className="text-emerald-700">
                Stock pasará de <span className="font-mono font-semibold">{insumo.stockActual.toFixed(2)}</span> a{' '}
                <span className="font-mono font-bold text-lg">{stockProyectado.toFixed(2)}</span> {insumo.unidad}
              </div>
            </div>
          )}

          {error && (
            <div className="rounded-lg bg-red-50 border border-red-200 p-3 text-sm text-red-700">
              {error}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 px-5 py-3 border-t border-slate-200 bg-slate-50 rounded-b-xl">
          <button onClick={onClose} className="btn-ghost" disabled={enviando}>
            Cancelar
          </button>
          <button
            onClick={onGuardar}
            disabled={enviando || !valido}
            className="btn-primary"
            style={{ minHeight: '44px' }}
          >
            <Plus className="h-4 w-4" />
            {enviando ? 'Guardando…' : 'Sumar al stock'}
          </button>
        </div>
      </div>
    </div>
  );
}
